import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ConfigProvider } from "antd";
import { HelmetProvider } from 'react-helmet-async'; // Make sure this is imported
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import "./App.css";

import ProtectedRoute from "./components/Auth/ProtectedRoute";
import Analytics from "./components/common/Analytics";
import CookieManager from "./components/common/CookieBanner";
import LocationSelectionModal from "./components/common/LocationSelectionModal";
import {
  autoDetectAndSetLocation,
  loadSavedLocation,
  selectHasSavedLocation,
} from "./redux/locationSlice";

import LoginPage from "./pages/Auth/LoginPage";
import ForgotPasswordPage from "./pages/Auth/ForgotPasswordPage";
import SignupPage from "./pages/Auth/SignupPage";
import UpdatePasswordPage from "./pages/Auth/UpdatePasswordPage";
import HomePage from "./pages/Home/HomePage";
import AboutUsPage from "./pages/CompanyInfos/AboutUsPage";
import ContactUsPage from "./pages/CompanyInfos/ContactUsPage";
import FAQPage from "./pages/CompanyInfos/FAQpage";
import HelpPage from "./pages/CompanyInfos/HelpPage";
import PrivacyPolicyPage from "./pages/CompanyInfos/PrivacyPolicyPage";
import CancellationPolicyPage from "./pages/CompanyInfos/CancellationPolicyPage";
import RefundPolicyPage from "./pages/CompanyInfos/RefundPolicyPage";
import TermsAndConditionsPage from "./pages/CompanyInfos/TermsAndConditionsPage";
import ComingSoonPage from "./pages/Fallback/ComingSoon";
import HostEventPage from "./pages/HostEvent/HostEventPage";
import TicketsPage from "./pages/Tickets/TicketsPage";
import DashboardPage from "./pages/Dashboard/DashboardPage";
import ProfilePage from "./pages/Profile/ProfilePage";
import EventsPage from "./pages/Events/EventsPage";
import EventAddEditPage from "./pages/Events/EventAddEditPage";
import EventDetailsPage from "./pages/Events/EventDetailsPage";
import CategoryEventsPage from "./pages/Events/CategoryEventsPage";
import TicketBookingPage from "./pages/Events/TicketBookingPage";
import PaymentsPage from "./pages/Events/PaymentsPage";
import VenuesPage from "./pages/Venues/VenuesPage";
import VenueAddEditPage from "./pages/Venues/VenueAddEditPage";
import VenueDetailsPage from "./pages/Venues/VenueDetailsPage";
import ContactsPage from "./pages/Contacts/ContactsPage";
import TemplatesPage from "./pages/Templates/TemplatesPage";
import InvitationBuilderPage from "./pages/InvitationBuilder/InvitationBuilderPage";
import InvitationSharePreviewPage from "./pages/InvitationBuilder/InvitationSharePreviewPage";
import InvitationRsvpsPage from "./pages/InvitationBuilder/InvitationRsvpsPage";
// import SettingsPage from "./pages/Settings/SettingsPage";

function LocationBootstrap() {
  const dispatch = useDispatch();
  const hasSavedLocation = useSelector(selectHasSavedLocation);

  useEffect(() => {
    // Load any saved location from localStorage into Redux
    dispatch(loadSavedLocation());
  }, [dispatch]);

  useEffect(() => {
    // If no saved location, silently attempt IP-based auto-detect
    if (!hasSavedLocation) {
      dispatch(autoDetectAndSetLocation());
    }
  }, [hasSavedLocation, dispatch]);

  return null;
}

export default function App() {
  return (
    <ConfigProvider
      wave={{ disabled: true }}
      theme={{
        token: {
          fontFamily: '"Inter", sans-serif',
          colorText: "#111827",
          colorTextSecondary: "#6B7280",
          colorPrimary: "#2563EB",
          colorInfo: "#2563EB",
          colorBorder: "#E5E7EB",
          colorBgBase: "#FFFFFF",
          fontSizeHeading1: "2.625rem", // 42px
          fontSizeHeading2: "1.5rem", // 32px
          fontSizeHeading3: "1.25rem", // 24px
          fontSizeHeading4: "1.125rem", // 18px
          fontSizeHeading5: "0.875rem", // 14px
          // controlHeight: 50,
          // controlHeightLG: 50,
        },
        components: {
          Button: {
            controlHeight: 42,
            controlHeightLG: 42,
            contentFontSize: 16,
            colorPrimary: "#2563EB",
            colorPrimaryHover: "#1D4ED8",
            colorPrimaryActive: "#1E40AF",
            borderRadius: 8,
          },
          Menu: {
            itemHoverBg: "#EFF6FF",
            itemActiveBg: "#EFF6FF",
            itemHoverColor: "#2563EB",
            itemSelectedColor: "#2563EB",
          },
          Input: {
            paddingInline: 12,
            colorBorder: "#E5E7EB",
            hoverBorderColor: "#2563EB",
            activeBorderColor: "#2563EB",
            colorTextPlaceholder: "rgba(107, 114, 128, 0.9)",
            colorText: "#111827",
            controlHeightLG: 42,
          },
          DatePicker: {
            colorBorder: "#E5E7EB",
            hoverBorderColor: "#2563EB",
            activeBorderColor: "#2563EB",
            colorTextPlaceholder: "rgba(107, 114, 128, 0.9)",
            colorText: "#111827",
            controlHeightLG: 42,
          },
          Select: {
            colorBorder: "#E5E7EB",
            hoverBorderColor: "#2563EB",
            activeBorderColor: "#2563EB",
            colorTextPlaceholder: "rgba(107, 114, 128, 0.9)",
            colorText: "#111827",
            optionActiveBg: "#EFF6FF",
            optionSelectedBg: "#EFF6FF",
            optionSelectedColor: "#2563EB",
            optionSelectedFontWeight: 500,
            controlHeightLG: 42,
          },
          Dropdown: {
            controlItemBgHover: "#EFF6FF",
            controlItemBgActive: "#EFF6FF",
            fontSize: "0.875rem",
            paddingBlock: 8,
            fontSizeIcon: 24,
          },
          Tag: {
            borderRadiusSM: 30,
          },
          Tabs: {
            cardBg: "transpatent",
            colorBorderSecondary: "transpatent",
            cardPadding: "14px 24px",
            fontSize: 16,
            itemActiveColor: "#2563EB",
            itemSelectedColor: "#2563EB",
          },
          Upload: {
            colorBorder: "#E5E7EB",
            hoverBorderColor: "#2563EB",
            lineType: "solid",
          },
          Table: {
            fontSize: 14,
            rowSelectedBg: "#F8FAFC",
            rowSelectedHoverBg: "#F1F5F9",
            lineType: "dashed",
          },
          Modal: {
            borderRadiusLG: 24,
            boxShadow:
              "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
          },
        },
      }}
    >
      <HelmetProvider>
        <BrowserRouter>
          <LocationBootstrap />
          <LocationSelectionModal />
          <Analytics />
          <Routes>
            {/* ROUTES ACCESSIBLE TO ALL USERS*/}
            <Route path="/" element={<HomePage />} />
            <Route path="/event-details/:slug" element={<EventDetailsPage />} />
            <Route path="/events/category/:slug" element={<CategoryEventsPage />} />
            <Route path="/about-us" element={<AboutUsPage />} />
            <Route path="/contact-us" element={<ContactUsPage />} />
            <Route path="/faqs" element={<FAQPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/cancellation-policy" element={<CancellationPolicyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
            <Route path="/coming-soon" element={<ComingSoonPage />} />
            <Route
              path="/invitation-builder/:id?"
              element={<InvitationBuilderPage />}
            />
            <Route
              path="/invitation-share/:code"
              element={<InvitationSharePreviewPage />}
            />

            <Route
              element={<ProtectedRoute allowedRoles={["user", "organizer"]} />}
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route
                path="/invitation-rsvps"
                element={<InvitationRsvpsPage />}
              />
              <Route path="/discover-events" element={<HomePage />} />
              <Route path="/host-event" element={<HostEventPage />} />
              <Route path="/event-details/booking/:slug" element={<TicketBookingPage />} />
              <Route path="/event-details/payment/:slug" element={<PaymentsPage />} />
              <Route path="/my-profile" element={<ProfilePage />} />
              <Route path="/my-tickets" element={<TicketsPage />} />
            </Route>

            {/* ORGANIZER USER */}
            <Route element={<ProtectedRoute allowedRoles={["organizer"]} />}>
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/create-new-event" element={<EventAddEditPage />} />
              <Route path="/edit-event/:id" element={<EventAddEditPage />} />
              <Route path="/venues" element={<VenuesPage />} />
              <Route path="/venue-details/:id" element={<VenueDetailsPage />} />
              <Route path="/create-new-venue" element={<VenueAddEditPage />} />
              <Route path="/edit-venue/:id" element={<VenueAddEditPage />} />
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/settings" element={<ComingSoonPage />} />
            </Route>

            {/* GUEST USER */}
            <Route element={<ProtectedRoute allowedRoles={["guest"]} />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/update-password" element={<UpdatePasswordPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </HelmetProvider>
      {/* The Cookie Manager - Place it at the bottom */}
      <CookieManager />
    </ConfigProvider>
  );
}
